import { PostType, StateTag } from "../RootPost";

export function stateTagIdFromState(state: string, postType: PostType): StateTag {
    switch (postType) {
        case PostType.suggestion:
            return stateTagIdeas[state] ?? StateTag.unknown;
        case PostType.problem:
            return stateTagProblems[state] ?? StateTag.unknown;
        default:
            throw new Error(`No statemap for type ${postType}, please add one if needed.`);
    }
}

export function stateFromStateTagId(stateTagId: StateTag, postType: PostType): string {
    switch (postType) {
        case PostType.suggestion:
            return ideasStateTag[stateTagId] ?? unknown;
        case PostType.problem:
            return problemsStateTag[stateTagId] ?? unknown;
        default:
            throw new Error(`No statemap for type ${postType}, please add one if needed.`);
    }
}

/** Determines if a state is closed or "final" (likely meaning votes and comments are locked) */
export function isClosedState(state: string): boolean {
    return state === ProblemStates.duplicate ||
        state === ProblemStates.fixed ||
        state === ProblemStates.lowerPriority ||
        state === ProblemStates.notEnoughInfo ||
        state === ProblemStates.notABug ||
        state === ProblemStates.otherProduct ||
        state === ProblemStates.closedByDesign ||
        state === ProblemStates.closedNotRepro ||
        state === ProblemStates.closedObsolete ||
        state === ProblemStates.wontFix ||
        state === ProblemStates.willNotFix ||
        state === IdeaStates.outOfScope ||
        state === IdeaStates.closed ||
        state === IdeaStates.completed ||
        state === IdeaStates.onBacklog ||
        state === IdeaStates.closedObsolete;
}

export class ProblemStates {
    public static readonly fixed: string = "fixed";
    public static readonly duplicate: string = "duplicate";
    public static readonly wontFix: string = "wont-fix";
    public static readonly willNotFix: string = "will-not-fix";
    public static readonly moreInfo: string = "more-info";
    public static readonly new: string = "new";
    public static readonly investigation: string = "investigation";
    public static readonly triaged: string = "triaged";
    public static readonly pending: string = "pending";
    public static readonly notABug: string = "notabug";
    public static readonly notEnoughInfo: string = "notenoughinfo";
    public static readonly invalid: string = "invalid";

    // new states for VS 2017 RTW:
    public static readonly future: string = "future";
    public static readonly closedFuture: string = "closed-future";
    public static readonly lowerPriority: string = "lowerpriority";
    public static readonly otherProduct: string = "otherproduct";
    public static readonly infoGiven: string = "info-given";
    public static readonly pendingRelease: string = "pending-release";
    public static readonly reopen: string = "reopen";

    // new states for VS 2017 version 15.4 preview 2
    public static readonly consideration: string = "consideration";
    public static readonly suggestion: string = "suggestion";
    // OXO specific state
    public static readonly active: string = "active";
    public static readonly closedByDesign: string = "closedbydesign";
    public static readonly closedNotRepro: string = "closednotrepro";
    public static readonly closedObsolete: string = "closedobsolete";
}

export class IdeaStates {
    public static readonly closed: string = "closed";
    public static readonly duplicate: string = "duplicate";
    public static readonly notEnoughInfo: string = "notenoughinfo";
    public static readonly otherProduct: string = "otherproduct";
    public static readonly outOfScope: string = "outofscope";
    public static readonly pending: string = "pending";
    public static readonly fixed: string = "fixed";
    public static readonly moreInfo: string = "moreinfo";
    public static readonly new: string = "new";
    public static readonly investigation: string = "investigation";
    public static readonly consideration: string = "consideration";
    public static readonly invalid: string = "invalid";
    // OXO specific states
    public static readonly active: string = "active";
    public static readonly closedObsolete: string = "closedobsolete";
    public static readonly completed: string = "completed";
    public static readonly onBacklog: string = "onbacklog";

}

const unknown: string = "unknown";

const stateTagProblems: { [stateKey: string]: StateTag } = {
    [ProblemStates.fixed]: StateTag.fixed,
    [ProblemStates.duplicate]: StateTag.duplicate,
    [ProblemStates.wontFix]: StateTag.unknown,
    [ProblemStates.moreInfo]: StateTag.more_info,
    [ProblemStates.new]: StateTag.new,
    [ProblemStates.investigation]: StateTag.investigation,
    [ProblemStates.triaged]: StateTag.triaged,
    [ProblemStates.pending]: StateTag.pending,
    [ProblemStates.notABug]: StateTag.not_a_bug,
    [ProblemStates.notEnoughInfo]: StateTag.not_enough_info,

    // new states for VS 2017 RTW:
    [ProblemStates.future]: StateTag.unknown,
    [ProblemStates.closedFuture]: StateTag.unknown,
    [ProblemStates.lowerPriority]: StateTag.lower_priority,
    [ProblemStates.otherProduct]: StateTag.other_product,
    [ProblemStates.infoGiven]: StateTag.unknown,
    [ProblemStates.pendingRelease]: StateTag.pending,
    [ProblemStates.reopen]: StateTag.unknown,

    // new states for VS 2017 version 15.4 preview 2
    [ProblemStates.consideration]: StateTag.consideration,
    [ProblemStates.suggestion]: StateTag.unknown,
    // OXO specific states
    [ProblemStates.active]: StateTag.active,
    [ProblemStates.closedByDesign]: StateTag.closed_by_design,
    [ProblemStates.closedNotRepro]: StateTag.closed_not_repro,
    [ProblemStates.closedObsolete]: StateTag.closed_obsolete,
    [ProblemStates.wontFix]: StateTag.wont_fix,
    [ProblemStates.willNotFix]: StateTag.will_not_fix,
};

const stateTagIdeas: { [stateKey: string]: StateTag } = {
    [IdeaStates.closed]: StateTag.closed,
    [IdeaStates.duplicate]: StateTag.duplicate,
    [IdeaStates.notEnoughInfo]: StateTag.not_enough_info,
    [IdeaStates.otherProduct]: StateTag.other_product,
    [IdeaStates.pending]: StateTag.pending,
    [IdeaStates.fixed]: StateTag.fixed,
    [IdeaStates.moreInfo]: StateTag.more_info,
    [IdeaStates.new]: StateTag.new,
    [IdeaStates.investigation]: StateTag.investigation,
    [IdeaStates.consideration]: StateTag.consideration,
    [IdeaStates.outOfScope]: StateTag.out_of_scope,
    // OXO specific state
    [IdeaStates.active]: StateTag.active,
    [IdeaStates.closedObsolete]: StateTag.closed_obsolete,
    [IdeaStates.completed]: StateTag.completed,
    [IdeaStates.onBacklog]: StateTag.on_backlog,

};

const problemsStateTag: { [stateKey: number]: string } = {
    [StateTag.unknown]: unknown,
    [StateTag.new]: ProblemStates.new,
    [StateTag.triaged]: ProblemStates.triaged,
    [StateTag.consideration]: ProblemStates.consideration,
    [StateTag.investigation]: ProblemStates.investigation,
    [StateTag.more_info]: ProblemStates.moreInfo,
    [StateTag.pending]: ProblemStates.pending,
    [StateTag.closed]: "closed",
    [StateTag.fixed]: ProblemStates.fixed,
    [StateTag.duplicate]: ProblemStates.duplicate,
    [StateTag.lower_priority]: ProblemStates.lowerPriority,
    [StateTag.not_enough_info]: ProblemStates.notEnoughInfo,
    [StateTag.not_a_bug]: ProblemStates.notABug,
    [StateTag.other_product]: ProblemStates.otherProduct,
    [StateTag.out_of_scope]: "out_of_scope",
    // OXO specific state
    [StateTag.active]: ProblemStates.active,
    [StateTag.closed_by_design]: ProblemStates.closedByDesign,
    [StateTag.closed_not_repro]: ProblemStates.closedNotRepro,
    [StateTag.closed_obsolete]: ProblemStates.closedObsolete,
    [StateTag.wont_fix]: ProblemStates.wontFix,
    [StateTag.will_not_fix]: ProblemStates.willNotFix,
};

const ideasStateTag: { [stateKey: number]: string } = {
    [StateTag.unknown]: unknown,
    [StateTag.new]: IdeaStates.new,
    [StateTag.triaged]: "triaged",
    [StateTag.consideration]: IdeaStates.consideration,
    [StateTag.investigation]: IdeaStates.investigation,
    [StateTag.more_info]: IdeaStates.moreInfo,
    [StateTag.pending]: IdeaStates.pending,
    [StateTag.closed]: IdeaStates.closed,
    [StateTag.fixed]: IdeaStates.fixed,
    [StateTag.duplicate]: IdeaStates.duplicate,
    [StateTag.lower_priority]: "lower_priority",
    [StateTag.not_enough_info]: IdeaStates.notEnoughInfo,
    [StateTag.not_a_bug]: "not_a_bug",
    [StateTag.other_product]: IdeaStates.otherProduct,
    [StateTag.out_of_scope]: IdeaStates.outOfScope,
    // OXO specific states
    [StateTag.active]: IdeaStates.active,
    [StateTag.closed_obsolete]: IdeaStates.closedObsolete,
    [StateTag.completed]: IdeaStates.completed,
    [StateTag.on_backlog]: IdeaStates.onBacklog,
};